import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalleryDto, GalleryTheme, Section } from '@nusign/picflow-backend-types';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { EmbedDisplayComponent } from '../../../gallery-shared/components/embed-display/embed-display.component';
import { sectionWidths } from '../../../lib/default-asset-section';
import { SectionService } from '../../../services/section.service';
import { ToastService } from '../../../services/toast.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { CommonSectionSettingsComponent } from '../common-section-settings/common-section-settings.component';
import { SectionContainerComponent } from '../section-container/section-container.component';

@Component({
  selector: 'app-edit-embed-section',
  templateUrl: './edit-embed-section.component.html',
  styleUrls: ['./edit-embed-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SectionContainerComponent,
    ContextMenuComponent,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    InputFieldComponent,
    EmbedDisplayComponent,
    CommonSectionSettingsComponent,
  ],
})
export class EditEmbedSectionComponent implements OnInit {
  private sectionService = inject(SectionService);
  private toasts = inject(ToastService);
  @Input() sectionIndex: number;
  @Input() gallery: GalleryDto;
  @Input() section: Section;
  @Input({ required: true }) theme: GalleryTheme;
  @Input() sections: Section[];
  @Input() darkBackground: boolean;
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  @Output() addSection: EventEmitter<number> = new EventEmitter();

  isDesktop = signal(ScreenSizes.screenSize() === 'desktop');

  @HostListener('window:resize', ['$event']) winResize() {
    this.isDesktop.set(ScreenSizes.screenSize() === 'desktop');
  }

  form = new FormGroup({ url: new FormControl() });
  url = signal<URL>(undefined);

  imageSectionValues = {
    widths: sectionWidths,
  };

  ngOnInit(): void {
    this.form.setValue({ url: this.section.embed_url });
    this.url.set(this.getUrlObj(this.section.embed_url));
  }

  parse() {
    this.section.embed_url = this.form.value.url;
    if (!this.section.embed_url) return;
    this.url.set(this.getUrlObj(this.section.embed_url));
  }

  async update() {
    const oldValue = this.section.embed_url;
    this.parse();
    if (this.url) this.save();
    else {
      this.toasts.add('Invalid URL', 'error');
      this.section.embed_url = oldValue;
    }
  }

  async save() {
    this.section.embed_url = this.url()?.href || null;
    this.form.setValue({ url: this.url()?.href || null });
    this.sectionService
      .updateSection(this.section, {
        embed_url: this.section.embed_url,
      })
      .subscribe();
  }

  reset() {
    this.section.embed_url = null;
    this.form.setValue({ url: null });
    this.url.set(undefined);
  }

  getUrlObj(_url: string): URL {
    if (!_url) return;
    _url = _url.split("'").join('"');
    if (_url.startsWith('<blockquote class="twitter-tweet"')) {
      try {
        _url =
          'https://twitter.com/' + _url.split('<a href="https://twitter.com/').slice(-2)[1].split('?')[0].split('"')[0];
      } catch {
        console.warn('Invalid twitter link');
        return;
      }
    }

    if (_url.startsWith('<blockquote class="instagram-media"')) {
      try {
        _url = _url.split('data-instgrm-permalink="')[1].split('"')[0];
      } catch {
        console.warn('Invalid instagram link');
        return;
      }
    }

    if (_url.includes('<iframe')) {
      _url = _url.split('<iframe')[1].split('</iframe')[0];
      const width = _url.includes('width="') ? parseInt(_url.split('width="')[1].split('"')[0]) : undefined;
      const height = _url.includes('height="') ? parseInt(_url.split('height="')[1].split('"')[0]) : undefined;
      _url = _url.split('src="')?.[1].split('"')[0];
      if (!_url) return;
      if (!_url.includes('?')) _url += '?a';
      _url += `&p_width=${width}&p_height=${height}`;
      console.debug(_url);
    }

    if (_url.includes('data-kuula')) {
      _url = _url.split('data-kuula="')[1].split('"')[0];
    }

    try {
      return new URL(_url);
    } catch {}
  }
}
